import React from 'react';
import { Box, Typography, LinearProgress, Skeleton } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { TodayDashboardResponse } from '@/types/dashboard.types';

// ─── VerticalBar ──────────────────────────────────────────────────────────────

interface ColumnProps {
  emoji: string;
  pct: number;
  current: number;
  goal: number;
  label: string;
  color: string;
}

const VerticalBar: React.FC<ColumnProps> = ({ emoji, pct, current, goal, label, color }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
    <Typography sx={{ fontSize: 16, lineHeight: 1 }}>{emoji}</Typography>
    <Box sx={{
      width: 20, height: 62,
      borderRadius: '10px',
      bgcolor: 'action.hover',
      overflow: 'hidden',
      display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      flexShrink: 0,
    }}>
      <Box sx={{
        width: '100%',
        height: `${Math.min(pct, 100)}%`,
        bgcolor: color,
        transition: 'height 0.5s ease',
      }} />
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.2, fontSize: '0.6rem' }}>
      {current}<br />{goal} {label}
    </Typography>
  </Box>
);

// ─── SummaryRing ──────────────────────────────────────────────────────────────

interface SummaryRingProps {
  emoji: string;
  pct: number;
  current: number;
  goal: number;
  label: string;
  color: string;
}

const SummaryRing: React.FC<SummaryRingProps> = ({ emoji, pct, current, goal, label, color }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={68}
        thickness={4}
        sx={{ color: 'action.hover', position: 'absolute', top: 0, left: 0 }}
      />
      <CircularProgress
        variant="determinate"
        value={Math.min(pct, 100)}
        size={68}
        thickness={4}
        sx={{ color }}
      />
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <Typography sx={{ fontSize: 17, lineHeight: 1 }}>{emoji}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '0.6rem' }}>{pct}%</Typography>
      </Box>
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.2, fontSize: '0.6rem' }}>
      {current}<br />{goal} {label}
    </Typography>
  </Box>
);

// ─── MacrosBlock ──────────────────────────────────────────────────────────────

interface MacroValues {
  proteins: { current: number; goal: number };
  fats:     { current: number; goal: number };
  carbs:    { current: number; goal: number };
}

const MACRO_META = [
  { key: 'proteins' as const, label: 'Б', color: '#e53935' },
  { key: 'fats'     as const, label: 'Ж', color: '#fb8c00' },
  { key: 'carbs'    as const, label: 'В', color: '#43a047' },
];

const MacrosBlock: React.FC<{ macros: MacroValues }> = ({ macros }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, justifyContent: 'center' }}>
    {MACRO_META.map(({ key, label, color }) => {
      const { current, goal } = macros[key];
      const pct = goal > 0 ? Math.min(Math.round((current / goal) * 100), 100) : 0;
      return (
        <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontWeight: 700, color, fontSize: '0.65rem', width: 11, flexShrink: 0 }}>
            {label}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              width: 36, height: 5, borderRadius: 3,
              bgcolor: 'action.hover',
              '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
            }}
          />
          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', width: 22, textAlign: 'right', flexShrink: 0 }}>
            {current}г
          </Typography>
        </Box>
      );
    })}
  </Box>
);

// ─── Skeleton placeholder ──────────────────────────────────────────────────────

const RingsSkeleton: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 2 }}>
    {[68, 68, 80, 68, 68].map((size, i) => (
      <Skeleton key={i} variant="circular" width={size} height={size} />
    ))}
  </Box>
);

// ─── SummaryRings ─────────────────────────────────────────────────────────────

interface SummaryRingsProps {
  date: string;
  dashboardData?: TodayDashboardResponse | null;
  loading?: boolean;
}

export const SummaryRings: React.FC<SummaryRingsProps> = ({ dashboardData, loading }) => {
  if (loading && !dashboardData) {
    return (
      <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, mx: 1, mb: 2, boxShadow: 1 }}>
        <RingsSkeleton />
      </Box>
    );
  }

  const fluid    = dashboardData?.fluid?.summary;
  const calories = dashboardData?.food?.summary?.calories;
  const protein  = dashboardData?.food?.summary?.protein;
  const fat      = dashboardData?.food?.summary?.fat;
  const carbs    = dashboardData?.food?.summary?.carbs;
  const workouts = dashboardData?.workouts?.summary;
  const tasksSummary = dashboardData?.tasks?.summary as any;

  const waterPct = fluid    ? Math.round(fluid.percentage)    : 0;
  const calPct   = calories ? Math.round(calories.percentage) : 0;
  const actPct   = workouts ? Math.round(workouts.percentage) : 0;

  const tasksTotal = tasksSummary?.total ?? 0;
  const tasksDone  = tasksSummary?.completed ?? 0;
  const tasksPct   = tasksTotal > 0 ? Math.round((tasksDone / tasksTotal) * 100) : 0;

  const macros: MacroValues = {
    proteins: { current: Math.round(protein?.actual ?? 0), goal: Math.round(protein?.goal ?? 1) },
    fats:     { current: Math.round(fat?.actual ?? 0),     goal: Math.round(fat?.goal ?? 1) },
    carbs:    { current: Math.round(carbs?.actual ?? 0),   goal: Math.round(carbs?.goal ?? 1) },
  };

  return (
    <Box sx={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      px: 2, py: 2,
      bgcolor: 'background.paper',
      borderRadius: 2,
      mx: 1, mb: 2,
      boxShadow: 1,
    }}>
      <VerticalBar
        emoji="💧"
        pct={waterPct}
        current={fluid?.actual ?? 0}
        goal={fluid?.goal ?? 0}
        label="мл"
        color="#0288d1"
      />

      <SummaryRing
        emoji="⚡"
        pct={calPct}
        current={Math.round(calories?.actual ?? 0)}
        goal={Math.round(calories?.goal ?? 0)}
        label="ккал"
        color="#388e3c"
      />

      <MacrosBlock macros={macros} />

      <SummaryRing
        emoji="🔥"
        pct={actPct}
        current={workouts?.actual ?? 0}
        goal={workouts?.goal ?? 0}
        label="хв"
        color="#ef6c00"
      />

      <VerticalBar
        emoji="🎯"
        pct={tasksPct}
        current={tasksDone}
        goal={tasksTotal}
        label="зад."
        color="#7b1fa2"
      />
    </Box>
  );
};
